/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {FocusableOption} from '@angular/cdk/a11y';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  ViewEncapsulation,
} from '@angular/core';
import {CanDisable, MATERIAL_COMPATIBILITY_MODE, mixinDisabled} from '@angular/material/core';
import {Subject} from 'rxjs/Subject';
/**
 * \@docs-private
 */
export class MdMenuItemBase {}
export const /** @type {?} */ _MdMenuItemMixinBase = mixinDisabled(MdMenuItemBase);
/**
 * This directive is intended to be used inside an md-menu tag.
 * It exists mostly to set the role attribute.
 */
export class MdMenuItem extends _MdMenuItemMixinBase implements FocusableOption, CanDisable,
  OnDestroy {
/**
 * Stream that emits when the menu item is hovered.
 */
hover: Subject<MdMenuItem> = new Subject();
/**
 * Whether the menu item is highlighted.
 */
_highlighted: boolean = false;
/**
 * Whether the menu item acts as a trigger for a sub-menu.
 */
_triggersSubmenu: boolean = false;
/**
 * @param {?} _elementRef
 */
constructor(private _elementRef: ElementRef) {
    super();
  }
/**
 * Focuses the menu item.
 * @return {?}
 */
focus(): void {
    this._getHostElement().focus();
  }
/**
 * @return {?}
 */
ngOnDestroy() {
    this.hover.complete();
  }
/**
 * Used to set the `tabindex`.
 * @return {?}
 */
_getTabIndex(): string {
    return this.disabled ? '-1' : '0';
  }
/**
 * Returns the host DOM element.
 * @return {?}
 */
_getHostElement(): HTMLElement {
    return this._elementRef.nativeElement;
  }
/**
 * Prevents the default element actions if it is disabled.
 * @param {?} event
 * @return {?}
 */
_checkDisabled(event: Event): void {
    if (this.disabled) {
      event.preventDefault();
      event.stopPropagation();
    }
  }
/**
 * Emits to the hover stream.
 * @return {?}
 */
_emitHoverEvent() {
    if (!this.disabled) {
      this.hover.next(this);
    }
  }

static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: '[md-menu-item], [mat-menu-item]',
  inputs: ['disabled'],
  host: {
    'role': 'menuitem',
    'class': 'mat-menu-item',
    '[class.mat-menu-item-highlighted]': '_highlighted',
    '[class.mat-menu-item-submenu-trigger]': '_triggersSubmenu',
    '[attr.tabindex]': '_getTabIndex()',
    '[attr.aria-disabled]': 'disabled.toString()',
    '[attr.disabled]': 'disabled || null',
    '(click)': '_checkDisabled($event)',
    '(mouseenter)': '_emitHoverEvent()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
  templateUrl: 'menu-item.html',
  exportAs: 'mdMenuItem, matMenuItem',
  viewProviders: [{provide: MATERIAL_COMPATIBILITY_MODE, useValue: true}],
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
];
}

function MdMenuItem_tsickle_Closure_declarations() {
/** @type {?} */
MdMenuItem.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdMenuItem.ctorParameters;
/**
 * Stream that emits when the menu item is hovered.
 * @type {?}
 */
MdMenuItem.prototype.hover;
/**
 * Whether the menu item is highlighted.
 * @type {?}
 */
MdMenuItem.prototype._highlighted;
/**
 * Whether the menu item acts as a trigger for a sub-menu.
 * @type {?}
 */
MdMenuItem.prototype._triggersSubmenu;
/** @type {?} */
MdMenuItem.prototype._elementRef;
}



interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
